<template>
  <div
    class="relative w-full overflow-hidden"
    style="height: 30vh; min-height: 350px"
  >
    <!-- Background Image with Overlay -->
    <div
      class="absolute inset-0 restaurant-brand"
      :data-image-url="store.image || ''"
    >
      <img
        v-if="store.image"
        :src="store.image"
        alt="Restaurant Interior"
        class="w-full h-full object-cover md:object-contain"
      />
      <div
        v-else
        class="w-full h-full bg-gradient-to-br from-primary-600 to-primary-800"
      />
      <div
        class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10"
      />
    </div>

    <!-- Content -->
    <div class="absolute inset-0 flex flex-col justify-end pb-16">
      <div class="container mx-auto px-4 slideUp-0-6s">
        <div class="max-w-12xl">
          <div class="flex items-center gap-2 mb-3">
            <span
              v-if="store.description"
              class="bg-primary-600 text-white px-3 py-1 rounded-md text-xs font-bold tracking-wider uppercase"
            >
              Featured
            </span>
            <div
              v-if="store.rating"
              class="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-md text-white text-xs"
            >
              <Star :size="12" class="text-yellow-400" />
              <span class="font-bold">{{ store.rating }}</span>
            </div>
          </div>

          <h1
            class="text-2rem md:text-6xl lg:text-7xl font-serif font-bold text-white mb-4 leading-tight"
          >
            {{ store.name }}
          </h1>

          <p
            v-if="store.description"
            class="text-gray-200 text-lg md:text-xl mb-8 max-w-xl font-light"
          >
            {{ store.description }}
          </p>

          <div class="flex flex-wrap gap-6 text-white/90">
            <div v-if="store.workingHours" class="flex items-center gap-2">
              <Clock :size="18" class="text-primary-400" />
              <span class="font-medium">{{ workingHoursText }}</span>
              <span
                class="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                :class="isStoreOpen ? 'bg-green-600' : 'bg-red-600'"
              >
                {{ isStoreOpen ? "Open" : "Closed" }}
              </span>
            </div>
            <a
              v-if="store.address"
              :href="mapsUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-2 text-white/90 no-underline hover:text-white transition-colors"
            >
              <MapPin :size="18" class="text-primary-400" />
              <span class="font-medium">
                {{ addressLine1
                }}<template v-if="addressLine2"
                  ><br />{{ addressLine2 }}</template
                >
              </span>
              <span
                class="text-xs font-bold px-2 py-0.5 rounded-full bg-primary-600 text-white"
              >
                Navigate
              </span>
            </a>
            <a
              v-if="store.phone"
              :href="`tel:${telHref}`"
              class="flex items-center gap-2 text-white/90 no-underline hover:text-white transition-colors"
            >
              <Phone :size="18" class="text-primary-400" />
              <span class="font-medium">{{ store.phone }}</span>
              <span
                class="text-xs font-bold px-2 py-0.5 rounded-full bg-primary-600 text-white"
              >
                Call
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Star, Clock, MapPin, Phone } from "lucide-vue";

export default {
  name: "Hero",
  components: {
    Star,
    Clock,
    MapPin,
    Phone
  },
  props: {
    store: {
      type: Object,
      required: true,
      default: () => ({
        name: "",
        address: "",
        phone: "",
        image: null,
        description: "",
        rating: null,
        workingHours: null
      })
    },
    isStoreClosed: {
      type: Boolean,
      default: false
    },
    storeStatus: {
      type: String,
      default: "open"
    }
  },
  computed: {
    isStoreOpen() {
      // Only show "Open" when the store is actually open right now.
      // "schedule_only" (closed but accepting scheduled orders) and
      // "closed" should both display as "Closed".
      return this.storeStatus === "open";
    },
    workingHoursText() {
      // Drop a leading "Open from:" label; the badge conveys open/closed.
      return (this.store.workingHours || "").replace(/^\s*open from:\s*/i, "");
    },
    telHref() {
      // Keep a leading + but strip spaces, dashes, parens, etc.
      return (this.store.phone || "").replace(/[^\d+]/g, "");
    },
    mapsUrl() {
      const query = encodeURIComponent(this.store.address || "");
      return `https://www.google.com/maps/search/?api=1&query=${query}`;
    },
    addressLine1() {
      const address = this.store.address || "";
      const commaIndex = address.indexOf(",");
      return commaIndex === -1 ? address : address.slice(0, commaIndex + 1);
    },
    addressLine2() {
      const address = this.store.address || "";
      const commaIndex = address.indexOf(",");
      return commaIndex === -1 ? "" : address.slice(commaIndex + 1).trim();
    }
  }
};
</script>

<style>
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.slideUp-0-6s {
  animation: slideUp 0.6s ease-out;
}
</style>
